import { useEffect, useState, useMemo, useRef } from "react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import BrandIcon from "@/components/BrandIcon";
import { Link } from "react-router-dom";
import { CATEGORIES } from "@/pages/CategoryPage";

const API_CAT_TO_SLUG = Object.values(CATEGORIES).reduce((acc, c) => {
  acc[c.apiCategory] = c.slug;
  return acc;
}, {});

const CATEGORY_META = {
  Retail: { emoji: "🛍", tint: "#dbe7ff", ink: "#1e3a8a" },
  Tech: { emoji: "💻", tint: "#cfefff", ink: "#075985" },
  Gaming: { emoji: "🎮", tint: "#ede0ff", ink: "#5b21b6" },
  Food: { emoji: "🍽", tint: "#ffe4c4", ink: "#9a3412" },
  Fashion: { emoji: "👗", tint: "#ffd7e5", ink: "#9d174d" },
  Prepaid: { emoji: "💳", tint: "#c5f5d4", ink: "#14532d" },
  Beauty: { emoji: "💄", tint: "#ffd0d5", ink: "#9f1239" },
  Travel: { emoji: "✈", tint: "#c7dcfe", ink: "#1e40af" },
};

// Rate → color tier
function rateTier(rate) {
  if (rate >= 90) return { bg: "linear-gradient(135deg,#22c55e,#16a34a)", chip: "#dcfce7", ink: "#166534", label: "Top tier" };
  if (rate >= 80) return { bg: "linear-gradient(135deg,#346af8,#2554d6)", chip: "#dbeafe", ink: "#1e3a8a", label: "Great" };
  if (rate >= 70) return { bg: "linear-gradient(135deg,#f59e0b,#d97706)", chip: "#fef3c7", ink: "#78350f", label: "Solid" };
  return { bg: "linear-gradient(135deg,#64748b,#475569)", chip: "#e2e8f0", ink: "#334155", label: "Standard" };
}

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function AcceptedCardsPage() {
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("rate"); // "rate" | "alpha"
  const letterRefs = useRef({});

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data));
  }, []);

  const filtered = useMemo(() => {
    const list = brands.filter((b) => b.name.toLowerCase().includes(q.toLowerCase()));
    if (sortBy === "rate") {
      return [...list].sort((a, b) => (b.rates?.receipt ?? b.rate ?? 0) - (a.rates?.receipt ?? a.rate ?? 0));
    }
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [brands, q, sortBy]);

  // Group by first letter for A-Z view
  const alphaGroups = useMemo(() => {
    const groups = {};
    [...brands].sort((a, b) => a.name.localeCompare(b.name)).forEach((b) => {
      const letter = b.name[0].toUpperCase();
      if (!/[A-Z]/.test(letter)) return;
      if (!q || b.name.toLowerCase().includes(q.toLowerCase())) {
        (groups[letter] = groups[letter] || []).push(b);
      }
    });
    return groups;
  }, [brands, q]);

  // Top 3 leaderboard
  const leaderboard = useMemo(
    () => [...brands].sort((a, b) => (b.rates?.receipt ?? b.rate ?? 0) - (a.rates?.receipt ?? a.rate ?? 0)).slice(0, 3),
    [brands],
  );

  const topRate = useMemo(() => {
    let m = 0;
    brands.forEach((b) => { m = Math.max(m, b.rates?.receipt ?? b.rate ?? 0); });
    return m ? Math.round(m) : 92;
  }, [brands]);

  const jumpTo = (letter) => {
    const el = letterRefs.current[letter];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const activeLetters = new Set(Object.keys(alphaGroups));

  return (
    <>
      <SEO title="Accepted gift cards" description={`Directory of ${brands.length || 70}+ gift card brands GC SPARK accepts, sorted alphabetically with live rates.`} />

      {/* Hero + inline search */}
      <section className="search-home-hero-section" data-testid="cards-hero" style={{ paddingBottom: 30 }}>
        <div className="main-container w-container">
          <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 60, alignItems: "center" }} className="cards-hero-grid">
            <div>
              <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 18 }}>
                Card directory
              </div>
              <h1 className="h1 _700-bold" style={{ marginBottom: 22 }}>
                {brands.length || "70+"} brands, one place, <span style={{ color: "#346af8" }}>live rates</span>
              </h1>
              <p style={{ fontSize: 17, color: "#4a4d5f", lineHeight: 1.6, maxWidth: 520 }}>
                A complete A–Z directory of every gift card we accept. Search a name or jump straight to a letter — every estimate is live and reviewed by a person after submission.
              </p>

              <div style={{ position: "relative", marginTop: 28, maxWidth: 460 }}>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a brand…" data-testid="cards-search"
                  style={{ width: "100%", height: 54, padding: "0 24px", borderRadius: 90, border: "1px solid #d8dcec", background: "#fff", boxShadow: "0 20px 40px -24px rgba(41,45,85,.2)", fontSize: 15, outline: "none", color: "#0b0d17" }} />
              </div>
            </div>

            {/* Right side: rate leaderboard */}
            <div style={{ background: "#0b0d17", padding: 28, borderRadius: 24, color: "#fff", boxShadow: "0 40px 80px -30px rgba(11,13,23,.5)" }} data-testid="cards-leaderboard">
              <div style={{ fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase", color: "#8fadff", fontWeight: 700, marginBottom: 4 }}>Rate leaderboard</div>
              <div style={{ fontSize: 22, fontWeight: 800, fontFamily: '"Hanken Grotesk", sans-serif', marginBottom: 18 }}>Top payouts right now</div>
              <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                {leaderboard.map((b, i) => {
                  const rate = Math.round(b.rates?.receipt ?? b.rate ?? 0);
                  return (
                    <li key={b.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: 12, borderRadius: 14, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)" }}>
                      <span style={{ fontFamily: '"Hanken Grotesk", sans-serif', fontSize: 24, fontWeight: 800, color: i === 0 ? "#ffd74b" : "#8fadff", width: 28 }}>{i + 1}</span>
                      <span style={{ width: 40, height: 40, borderRadius: 10, background: b.color, overflow: "hidden", flexShrink: 0 }}>
                        <BrandIcon logoKey={b.logo_key} name={b.name} className="w-full h-full" />
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.name}</div>
                        <div style={{ fontSize: 12, color: "#aab0cc", marginTop: 2 }}>${b.min}–${b.max}</div>
                      </div>
                      <div style={{ fontFamily: '"Hanken Grotesk", sans-serif', fontSize: 22, fontWeight: 800, color: "#8fadff" }}>{rate}%</div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
        <style>{`@media (max-width: 900px) { .cards-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; } }`}</style>
      </section>

      {/* Category feature strip */}
      <section style={{ padding: "40px 0", background: "#fff" }} data-testid="cards-categories">
        <div className="main-container w-container">
          <div style={{ fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase", color: "#9a93a6", fontWeight: 700, marginBottom: 16 }}>Browse by category</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
            {Object.values(CATEGORIES).map((c) => {
              const meta = CATEGORY_META[c.apiCategory] || { emoji: "🎁", tint: "#eaf0ff", ink: "#346af8" };
              const count = brands.filter((b) => b.category === c.apiCategory).length;
              return (
                <Link key={c.slug} to={`/cards/${c.slug}`} data-testid={`cards-cat-tile-${c.slug}`}
                  style={{ display: "flex", flexDirection: "column", gap: 6, padding: 18, borderRadius: 18, background: meta.tint, textDecoration: "none", transition: "transform .2s ease" }}
                  className="cat-tile">
                  <span style={{ fontSize: 26 }}>{meta.emoji}</span>
                  <div style={{ fontWeight: 800, color: meta.ink, fontSize: 15 }}>{c.label}</div>
                  <div style={{ fontSize: 12, color: "#0b0d17", opacity: 0.5 }}>{count} brand{count === 1 ? "" : "s"}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sort toggle + A-Z jump nav */}
      <section style={{ padding: "30px 0 20px", background: "#f8f9fc", borderTop: "1px solid #eaeaf0", position: "sticky", top: 0, zIndex: 20, backdropFilter: "blur(10px)" }} data-testid="cards-toolbar">
        <div className="main-container w-container">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "#9a93a6", fontWeight: 700 }}>Sort by</span>
              <button onClick={() => setSortBy("rate")} data-testid="sort-rate"
                style={{ padding: "8px 16px", borderRadius: 90, fontSize: 13, fontWeight: 700, cursor: "pointer",
                  border: sortBy === "rate" ? "1px solid #0b0d17" : "1px solid #d8dcec",
                  background: sortBy === "rate" ? "#0b0d17" : "#fff", color: sortBy === "rate" ? "#fff" : "#0b0d17" }}>
                Highest rate
              </button>
              <button onClick={() => setSortBy("alpha")} data-testid="sort-alpha"
                style={{ padding: "8px 16px", borderRadius: 90, fontSize: 13, fontWeight: 700, cursor: "pointer",
                  border: sortBy === "alpha" ? "1px solid #0b0d17" : "1px solid #d8dcec",
                  background: sortBy === "alpha" ? "#0b0d17" : "#fff", color: sortBy === "alpha" ? "#fff" : "#0b0d17" }}>
                Alphabetical
              </button>
            </div>

            {sortBy === "alpha" && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }} data-testid="cards-az-nav">
                {ALPHABET.map((L) => {
                  const active = activeLetters.has(L);
                  return (
                    <button key={L} onClick={() => active && jumpTo(L)} disabled={!active}
                      style={{ width: 26, height: 26, borderRadius: 6, border: "none",
                        background: active ? "#fff" : "transparent",
                        color: active ? "#346af8" : "#c8ccd8",
                        fontWeight: 700, fontSize: 12, cursor: active ? "pointer" : "not-allowed",
                        boxShadow: active ? "0 4px 8px -4px rgba(52,106,248,.3)" : "none" }}>
                      {L}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MAIN: table view when sorted by rate, letter-grouped list when alphabetical */}
      <section style={{ padding: "40px 0 90px", background: "#fff" }} data-testid="cards-directory">
        <div className="main-container w-container">
          {sortBy === "rate" ? (
            <RateTable rows={filtered} />
          ) : (
            <AlphaDirectory groups={alphaGroups} letterRefs={letterRefs} />
          )}

          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: 80, color: "#9a93a6" }} data-testid="cards-empty">
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
              No brands match "{q}".
            </div>
          )}

          <p data-testid="cards-trademark-disclaimer"
            style={{ marginTop: 60, fontSize: 12.5, color: "#9a93a6", lineHeight: 1.65, textAlign: "center", maxWidth: 760, marginLeft: "auto", marginRight: "auto" }}>
            Brand names and logos are trademarks of their respective owners. Their appearance on this website does not imply sponsorship, endorsement, or partnership with GC SPARK.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="boss-section">
        <div className="boss-container">
          <div className="boss-cta-band">
            <h2>Don't see your brand?</h2>
            <p>We add new brands every month. Email us and we'll fast-track it.</p>
            <Link to="/contact" className="boss-btn">Request a brand</Link>
          </div>
        </div>
      </section>

      <style>{`
        .cat-tile:hover { transform: translateY(-3px); }
        .brand-row:hover { background: #f8f9fc !important; }
        .brand-row:hover .row-arrow { transform: translateX(4px); background: #346af8 !important; }
      `}</style>
    </>
  );
}

function RateTable({ rows }) {
  return (
    <div style={{ overflow: "hidden", borderRadius: 20, border: "1px solid #eaeaf0" }} data-testid="cards-rate-table">
      {/* Header */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 0.9fr 110px", gap: 14, padding: "16px 22px", background: "#0b0d17", color: "#8fadff", fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase" }}
        className="rt-header">
        <div>Brand</div>
        <div className="rt-col-hide-sm">Category</div>
        <div className="rt-col-hide-sm">Range</div>
        <div style={{ textAlign: "right" }}>Payout</div>
        <div />
      </div>
      {rows.map((b, i) => {
        const rate = Math.round(b.rates?.receipt ?? b.rate ?? 0);
        const tier = rateTier(rate);
        const meta = CATEGORY_META[b.category] || { tint: "#eaf0ff", ink: "#346af8" };
        return (
          <Link key={b.id} to={`/submit?brand=${b.slug}`} data-testid={`cards-row-${b.slug}`}
            className="brand-row"
            style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 0.9fr 110px", gap: 14, padding: "16px 22px", alignItems: "center", background: i % 2 === 0 ? "#fff" : "#fafbfe", borderTop: "1px solid #eaeaf0", textDecoration: "none", transition: "background .15s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <span style={{ width: 42, height: 42, borderRadius: 12, background: b.color, flexShrink: 0, overflow: "hidden", boxShadow: "0 4px 10px -4px rgba(0,0,0,.25)" }}>
                <BrandIcon logoKey={b.logo_key} name={b.name} className="w-full h-full" />
              </span>
              <span style={{ fontWeight: 700, color: "#0b0d17", fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.name}</span>
            </div>
            <div className="rt-col-hide-sm">
              <span style={{ display: "inline-block", padding: "5px 14px", borderRadius: 90, background: meta.tint, color: meta.ink, fontSize: 12, fontWeight: 800, letterSpacing: ".01em" }}>{b.category}</span>
            </div>
            <div className="rt-col-hide-sm" style={{ fontSize: 14, color: "#4a4d5f", fontFamily: "monospace" }}>${b.min}–${b.max}</div>
            <div style={{ textAlign: "right" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 90, background: tier.bg, color: "#fff", fontFamily: '"Hanken Grotesk", sans-serif', fontWeight: 800, fontSize: 15, boxShadow: "0 6px 14px -6px rgba(0,0,0,.25)" }}>
                {rate}%
              </span>
            </div>
            <div style={{ textAlign: "right" }}>
              <span className="row-arrow" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 90, background: "#0b0d17", color: "#fff", fontSize: 12.5, fontWeight: 700, transition: "all .2s" }}>Sell →</span>
            </div>
          </Link>
        );
      })}
      <style>{`@media (max-width: 700px) { .rt-col-hide-sm { display: none !important; } .rt-header, .brand-row { grid-template-columns: 1.4fr 0.7fr 70px !important; } }`}</style>
    </div>
  );
}

function AlphaDirectory({ groups, letterRefs }) {
  const letters = Object.keys(groups).sort();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 40 }} data-testid="cards-alpha-directory">
      {letters.map((L) => (
        <div key={L} ref={(el) => (letterRefs.current[L] = el)} id={`letter-${L}`} style={{ scrollMarginTop: 100 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
            <div style={{ width: 64, height: 64, borderRadius: 18, background: "linear-gradient(135deg, #346af8, #6b8afc)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: '"Hanken Grotesk", sans-serif', fontSize: 32, fontWeight: 800, boxShadow: "0 20px 30px -18px rgba(52,106,248,.6)" }}>
              {L}
            </div>
            <div>
              <div style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "#9a93a6", fontWeight: 700 }}>Section</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#0b0d17", fontFamily: '"Hanken Grotesk", sans-serif' }}>{groups[L].length} brand{groups[L].length === 1 ? "" : "s"}</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
            {groups[L].map((b) => {
              const rate = Math.round(b.rates?.receipt ?? b.rate ?? 0);
              const tier = rateTier(rate);
              return (
                <Link key={b.id} to={`/submit?brand=${b.slug}`} data-testid={`cards-alpha-${b.slug}`}
                  className="brand-row"
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 12, background: "#fff", border: "1px solid #eaeaf0", textDecoration: "none", transition: "all .2s" }}>
                  <span style={{ width: 38, height: 38, borderRadius: 10, background: b.color, flexShrink: 0, overflow: "hidden" }}>
                    <BrandIcon logoKey={b.logo_key} name={b.name} className="w-full h-full" />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: "#0b0d17", fontSize: 14.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.name}</div>
                    <div style={{ fontSize: 11.5, color: "#9a93a6", marginTop: 1 }}>{b.category}</div>
                  </div>
                  <span style={{ display: "inline-flex", padding: "5px 11px", borderRadius: 90, background: tier.chip, color: tier.ink, fontFamily: '"Hanken Grotesk", sans-serif', fontWeight: 800, fontSize: 13.5 }}>{rate}%</span>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
